import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import './themeCustomizer.css';

interface ColorPickerProps {
  label: string;
  colorKey: string;
  value: string;
  onChange: (colorKey: string, value: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ label, colorKey, value, onChange }) => {
  // Extract hex color from gradients and rgba values
  const extractColor = (colorValue: string): string => {
    if (colorValue.startsWith('#')) return colorValue;
    if (colorValue.startsWith('rgba')) {
      const match = colorValue.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        const [, r, g, b] = match;
        return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
      }
    }
    return '#000000';
  };

  return (
    <div className="color-picker-item">
      <label htmlFor={colorKey}>
        <span className="color-label">{label}</span>
        <div className="color-input-wrapper">
          <input
            type="color"
            id={colorKey}
            value={extractColor(value)}
            onChange={(e) => onChange(colorKey, e.target.value)}
            className="color-input"
          />
          <span className="color-value">{extractColor(value)}</span>
        </div>
      </label>
    </div>
  );
};

interface TypographyInputProps {
  label: string;
  typographyKey: string;
  value: string;
  onChange: (typographyKey: string, value: string) => void;
  type?: 'select' | 'range';
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

const TypographyInput: React.FC<TypographyInputProps> = ({ 
  label, 
  typographyKey, 
  value, 
  onChange, 
  type = 'select',
  options,
  min = 0.5,
  max = 4,
  step = 0.05,
  unit = 'rem'
}) => {
  if (type === 'select' && options) {
    return (
      <div className="typography-item">
        <label htmlFor={typographyKey}>
          <span className="typography-label">{label}</span>
          <select
            id={typographyKey}
            value={value}
            onChange={(e) => onChange(typographyKey, e.target.value)}
            className="typography-select"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  if (type === 'range') {
    const numericValue = parseFloat(value) || min;
    
    return (
      <div className="typography-item">
        <label htmlFor={typographyKey}>
          <div className="typography-range-header">
            <span className="typography-label">{label}</span>
            <span className="typography-range-value">{value}</span>
          </div>
          <input
            type="range"
            id={typographyKey}
            min={min}
            max={max}
            step={step}
            value={numericValue}
            onChange={(e) => onChange(typographyKey, `${e.target.value}${unit}`)}
            className="typography-range"
          />
          <div className="typography-range-labels">
            <span>{min}{unit}</span>
            <span>{max}{unit}</span>
          </div>
        </label>
      </div>
    );
  }

  return null;
};

const ThemeCustomizer: React.FC = () => {
  const { theme, updateCustomThemeColor, updateCustomThemeTypography, resetCustomTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'colors' | 'typography'>('colors');

  const handleColorChange = (colorKey: string, colorValue: string) => {
    // If not on custom theme, copy current theme to custom first
    if (theme.name !== 'custom') {
      // Copy current theme colors to custom theme
      Object.keys(theme.colors).forEach((key) => {
        updateCustomThemeColor(key, theme.colors[key as keyof typeof theme.colors]);
      });
      // Copy current theme typography to custom theme
      Object.keys(theme.typography).forEach((key) => {
        updateCustomThemeTypography(key, theme.typography[key as keyof typeof theme.typography]);
      });
      setTheme('custom');
    }
    updateCustomThemeColor(colorKey, colorValue);
  };

  const handleTypographyChange = (typographyKey: string, typographyValue: string) => {
    // If not on custom theme, copy current theme to custom first
    if (theme.name !== 'custom') {
      // Copy current theme colors to custom theme
      Object.keys(theme.colors).forEach((key) => {
        updateCustomThemeColor(key, theme.colors[key as keyof typeof theme.colors]);
      });
      // Copy current theme typography to custom theme
      Object.keys(theme.typography).forEach((key) => {
        updateCustomThemeTypography(key, theme.typography[key as keyof typeof theme.typography]);
      });
      setTheme('custom');
    }
    updateCustomThemeTypography(typographyKey, typographyValue);
  };

  const handleReset = () => {
    if (window.confirm('Скинути всі налаштування? Це поверне тему до стандартних значень.')) {
      resetCustomTheme();
    }
  };

  const handleSwitchToCustom = () => {
    // Copy current theme to custom theme before switching
    Object.keys(theme.colors).forEach((key) => {
      updateCustomThemeColor(key, theme.colors[key as keyof typeof theme.colors]);
    });
    Object.keys(theme.typography).forEach((key) => {
      updateCustomThemeTypography(key, theme.typography[key as keyof typeof theme.typography]);
    });
    setTheme('custom');
  };

  const colorGroups = [
    {
      title: 'Основні кольори',
      colors: [
        { key: 'primary' as keyof typeof theme.colors, label: 'Основний колір' },
        { key: 'primaryLight' as keyof typeof theme.colors, label: 'Світлий основний' },
        { key: 'primaryDark' as keyof typeof theme.colors, label: 'Темний основний' },
        { key: 'secondary' as keyof typeof theme.colors, label: 'Вторинний колір' },
      ],
    },
    {
      title: 'Фон',
      colors: [
        { key: 'background' as keyof typeof theme.colors, label: 'Фон сторінки' },
        { key: 'backgroundGradientStart' as keyof typeof theme.colors, label: 'Градієнт початок' },
        { key: 'backgroundGradientEnd' as keyof typeof theme.colors, label: 'Градієнт кінець' },
      ],
    },
    {
      title: 'Текст',
      colors: [
        { key: 'textPrimary' as keyof typeof theme.colors, label: 'Основний текст' },
        { key: 'textSecondary' as keyof typeof theme.colors, label: 'Вторинний текст' },
        { key: 'textHeading' as keyof typeof theme.colors, label: 'Заголовки' },
      ],
    },
    {
      title: 'Карточки та навігація',
      colors: [
        { key: 'cardBackground' as keyof typeof theme.colors, label: 'Фон карточок' },
        { key: 'cardBorder' as keyof typeof theme.colors, label: 'Межі карточок' },
        { key: 'navBackground' as keyof typeof theme.colors, label: 'Фон навігації' },
        { key: 'navBorder' as keyof typeof theme.colors, label: 'Межі навігації' },
      ],
    },
    {
      title: 'Посилання',
      colors: [
        { key: 'linkColor' as keyof typeof theme.colors, label: 'Колір посилань' },
        { key: 'linkHover' as keyof typeof theme.colors, label: 'Наведення на посилання' },
      ],
    },
    {
      title: 'Статуси',
      colors: [
        { key: 'success' as keyof typeof theme.colors, label: 'Успіх' },
        { key: 'warning' as keyof typeof theme.colors, label: 'Попередження' },
        { key: 'info' as keyof typeof theme.colors, label: 'Інформація' },
      ],
    },
  ];

  const fontFamilyOptions = [
    { value: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif', label: 'Системний (Sans-serif)' },
    { value: 'Georgia, "Times New Roman", Times, serif', label: 'Georgia (Serif)' },
    { value: '"Arial", "Helvetica", sans-serif', label: 'Arial' },
    { value: '"Verdana", "Geneva", sans-serif', label: 'Verdana' },
    { value: '"Trebuchet MS", "Helvetica", sans-serif', label: 'Trebuchet' },
    { value: '"Comic Sans MS", "Comic Sans", cursive', label: 'Comic Sans' },
    { value: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace', label: 'Monospace' },
  ];

  const fontWeightOptions = [
    { value: '300', label: 'Тонкий (300)' },
    { value: '400', label: 'Нормальний (400)' },
    { value: '500', label: 'Середній (500)' },
    { value: '600', label: 'Напівжирний (600)' },
    { value: '700', label: 'Жирний (700)' },
    { value: '800', label: 'Екстра жирний (800)' },
  ];

  const lineHeightOptions = [
    { value: '1.2', label: 'Щільно (1.2)' },
    { value: '1.4', label: 'Зручно (1.4)' },
    { value: '1.6', label: 'Нормально (1.6)' },
    { value: '1.8', label: 'Вільно (1.8)' },
    { value: '2.0', label: 'Широко (2.0)' },
  ];

  const letterSpacingOptions = [
    { value: 'normal', label: 'Нормальний (normal)' },
    { value: '-0.02em', label: 'Щільний (-0.02em)' },
    { value: '0.02em', label: 'Вільний (0.02em)' },
    { value: '0.05em', label: 'Широкий (0.05em)' },
    { value: '0.1em', label: 'Дуже широкий (0.1em)' },
  ];

  const typographyGroups = [
    {
      title: 'Шрифти',
      items: [
        { key: 'fontFamily' as keyof typeof theme.typography, label: 'Основний шрифт', type: 'select' as const, options: fontFamilyOptions },
        { key: 'fontFamilyHeading' as keyof typeof theme.typography, label: 'Шрифт заголовків', type: 'select' as const, options: fontFamilyOptions },
        { key: 'fontFamilyMono' as keyof typeof theme.typography, label: 'Моноширинний шрифт', type: 'select' as const, options: fontFamilyOptions },
      ],
    },
    {
      title: 'Розміри шрифтів',
      items: [
        { key: 'fontSizeBase' as keyof typeof theme.typography, label: 'Базовий розмір', type: 'range' as const, min: 0.8, max: 1.5, step: 0.05, unit: 'rem' },
        { key: 'fontSizeSmall' as keyof typeof theme.typography, label: 'Маленький текст', type: 'range' as const, min: 0.7, max: 1.2, step: 0.05, unit: 'rem' },
        { key: 'fontSizeLarge' as keyof typeof theme.typography, label: 'Великий текст', type: 'range' as const, min: 1.1, max: 2, step: 0.05, unit: 'rem' },
        { key: 'fontSizeH1' as keyof typeof theme.typography, label: 'Заголовок H1', type: 'range' as const, min: 1.8, max: 4, step: 0.1, unit: 'rem' },
        { key: 'fontSizeH2' as keyof typeof theme.typography, label: 'Заголовок H2', type: 'range' as const, min: 1.5, max: 3.5, step: 0.1, unit: 'rem' },
        { key: 'fontSizeH3' as keyof typeof theme.typography, label: 'Заголовок H3', type: 'range' as const, min: 1.2, max: 3, step: 0.05, unit: 'rem' },
      ],
    },
    {
      title: 'Вага шрифту',
      items: [
        { key: 'fontWeightNormal' as keyof typeof theme.typography, label: 'Нормальний', type: 'select' as const, options: fontWeightOptions },
        { key: 'fontWeightMedium' as keyof typeof theme.typography, label: 'Середній', type: 'select' as const, options: fontWeightOptions },
        { key: 'fontWeightBold' as keyof typeof theme.typography, label: 'Жирний', type: 'select' as const, options: fontWeightOptions },
      ],
    },
    {
      title: 'Висота рядка',
      items: [
        { key: 'lineHeightBase' as keyof typeof theme.typography, label: 'Базова висота', type: 'select' as const, options: lineHeightOptions },
        { key: 'lineHeightHeading' as keyof typeof theme.typography, label: 'Висота заголовків', type: 'select' as const, options: lineHeightOptions },
      ],
    },
    {
      title: 'Міжлітерний інтервал',
      items: [
        { key: 'letterSpacingNormal' as keyof typeof theme.typography, label: 'Нормальний', type: 'select' as const, options: letterSpacingOptions },
        { key: 'letterSpacingWide' as keyof typeof theme.typography, label: 'Широкий', type: 'select' as const, options: letterSpacingOptions },
      ],
    },
  ];

  return (
    <div className="theme-customizer">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="customizer-toggle"
        aria-label="Налаштування кольорів теми"
        title="Налаштування кольорів"
      >
        🎨
      </button>

      {isOpen && (
        <>
          <div className="customizer-overlay" onClick={() => setIsOpen(false)} />
          <div className="customizer-panel">
            <div className="customizer-header">
              <h3>Налаштування теми</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="close-button"
                aria-label="Закрити"
              >
                ✕
              </button>
            </div>

            <div className="customizer-tabs">
              <button
                className={`tab-button ${activeTab === 'colors' ? 'active' : ''}`}
                onClick={() => setActiveTab('colors')}
              >
                Кольори
              </button>
              <button
                className={`tab-button ${activeTab === 'typography' ? 'active' : ''}`}
                onClick={() => setActiveTab('typography')}
              >
                Типографіка
              </button>
            </div>

            <div className="customizer-content">
              {theme.name !== 'custom' && (
                <div className="customizer-warning">
                  <p>
                    ⚠️ Ви використовуєте тему "{theme.displayName}". 
                    Щоб налаштувати кольори та шрифти, перейдіть на "Власну тему".
                  </p>
                  <button 
                    onClick={handleSwitchToCustom} 
                    className="switch-to-custom-button"
                  >
                    Перейти на власну тему
                  </button>
                </div>
              )}
              
              <div className="customizer-info">
                <p>
                  {activeTab === 'colors' 
                    ? 'Налаштуйте кольори теми на свій смак. Зміни зберігаються автоматично.'
                    : 'Налаштуйте шрифти, розміри та інші параметри тексту. Зміни зберігаються автоматично.'}
                </p>
              </div>

              {activeTab === 'colors' && colorGroups.map((group) => (
                <div key={group.title} className="color-group">
                  <h4>{group.title}</h4>
                  {group.colors.map((color) => (
                    <ColorPicker
                      key={color.key}
                      label={color.label}
                      colorKey={color.key}
                      value={theme.colors[color.key]}
                      onChange={handleColorChange}
                    />
                  ))}
                </div>
              ))}

              {activeTab === 'typography' && typographyGroups.map((group) => (
                <div key={group.title} className="typography-group">
                  <h4>{group.title}</h4>
                  {group.items.map((item) => (
                    <TypographyInput
                      key={item.key}
                      label={item.label}
                      typographyKey={item.key}
                      value={theme.typography[item.key]}
                      onChange={handleTypographyChange}
                      type={item.type}
                      options={'options' in item ? item.options : undefined}
                      min={'min' in item ? item.min : undefined}
                      max={'max' in item ? item.max : undefined}
                      step={'step' in item ? item.step : undefined}
                      unit={'unit' in item ? item.unit : undefined}
                    />
                  ))}
                </div>
              ))}

              <div className="customizer-actions">
                <button onClick={handleReset} className="reset-button">
                  Скинути налаштування
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeCustomizer;
